/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import { atom } from 'jotai';
import { useAtom, useAtomValue } from 'jotai';
import { atomFamily } from 'jotai-family';
import { loadable } from '../utils/loadable';
import type { Feature, Polygon } from 'geojson';
import { Legend } from '../components/Legend';
import { allPostChunksSelector } from './selectors';
import { POSTProperties } from './types';
import type { RGBAColor } from '../map/deckgl-types';
import { useGridCellLayer } from '../map/GridCellLayer';
import React from 'react';
import { WowPostProperties } from '../wow/types';
import { useMatch } from 'react-router-dom';
import { selectedElevationKeyAtom } from '../map/atoms';
import { PrimaryLayer } from '../pages/MapPage';
import { selectedFeatureAtom } from '../Incident/FeaturePopup';
import { numberProperty } from '../utils/featureProperties';
import type { AttributeOptions } from '../map/attributeOptions';
import { PERCENTILE_LEGEND_ITEMS, type RankGroupOf } from '../map/percentileRank';

type Props = {
  extruded: boolean;
  visible: boolean;
};

export const postThreeDAtom = atom(false);

// export const selectedFeatureAtom = atom<Feature<
//   Polygon,
//   Partial<POSTProperties> &
//     Partial<CanopyProperties> &
//     Partial<WowPostProperties> &
//     Partial<NifcPostProperties>
// > | null>({
//   key: 'selectedFeature',
//   default: null,
// });

export const defaultSectionKey = (section: string) => {
  switch (section) {
    case 'nifc-post':
    case 'wow-post':
    case 'canopy-post':
    case 'ipaws-post':
    case 'post':
      return 'priority_rank';
    case 'energy':
      return 'nf_np_impact';
    case 'population':
      return 'exposurescore';
    case 'commodities':
      return 'peopleinneed';
    case 'communications':
      return 'percentout';
    case 'residential':
      return 'exposurescoreformap';
    case 'nonResidentialStructures':
      return 'exposurescore_allstructures';
    case 'healthMedical':
      return 'impactscore';
    case 'transportation':
      return 'impactscore';
    case 'foodWaterShelter':
      return 'sheltercapacitypercent';
    case 'declarations':
      return 'designation_type';
    default:
      return 'priority_rank';
  }
};

export const primaryKeyForSection = atomFamily((param: string) => atom(defaultSectionKey(param)));

/**
 * The group a POST grid cell's "Color by" percentile colouring ranks it
 * within: exactly the group each workflow ranks POST priority within
 * (priority_rank in workflows/shared), so the colouring and the priority read
 * the same population. WoW and IPAWS number each connected piece of a threat
 * or alert event_id (unify_threats, fetch_and_process_ipaws); NIFC ranks
 * within the fire; Canopy ranks within event_name, the day its swaths were
 * published.
 */
export const POST_EVENT_OF: Record<'wow' | 'nifc' | 'canopy' | 'ipaws', RankGroupOf> = {
  wow: properties => properties?.event_id,
  nifc: properties => properties?.incidentname,
  canopy: properties => properties?.event_name,
  ipaws: properties => properties?.event_id,
};

export const usePostLayer = (props: Props) => {
  const match = useMatch({ path: '/' });
  const allPostData = useAtomValue(loadable(allPostChunksSelector(match != null)));

  const [selectedPost] = useAtom(selectedFeatureAtom);
  const [selectedElevationKey] = useAtom(selectedElevationKeyAtom);
  const primaryKey = useAtomValue(primaryKeyForSection('ipaws-post'));

  const getFillColor = React.useCallback((d: Feature<Polygon, POSTProperties>): RGBAColor => {
    return getPostFillColor(d);
  }, []);

  const fillColor = React.useMemo(
    () => (primaryKey === 'priority_rank' ? getFillColor : undefined),
    [primaryKey, getFillColor]
  );

  const getElevation = React.useCallback(
    (d: Feature<Polygon, POSTProperties>): number =>
      numberProperty(d?.properties, selectedElevationKey),
    [selectedElevationKey]
  );

  // const handleClick = React.useCallback(
  //   (info: any) => {
  //     if (info?.object) {
  //       setSelectedPost(info.object);
  //     }
  //   },
  //   [setSelectedPost]
  // );

  const GridCellLayer = useGridCellLayer<
    Feature<Polygon, POSTProperties & Partial<WowPostProperties>>
  >({
    id: 'ipaws-post',
    data: (allPostData.state === 'hasData' ? allPostData.data : []) as unknown as Feature<
      Polygon,
      POSTProperties & Partial<WowPostProperties>
    >[],
    getFillColor: fillColor,
    eventOf: POST_EVENT_OF.ipaws,
    highlightId: selectedPost?.id,
    getElevation,
    extruded: props.extruded,
    visible: props.visible,
    // onClick: handleClick,
  });

  return GridCellLayer;
};

export const postColorRange: RGBAColor[] = [
  [181, 9, 9, 190], // #B50909
  [219, 129, 0, 190], // #DB8100
  [226, 178, 0, 190], // #E2B200
  [143, 195, 201, 190], // #8FC3C9
  [95, 133, 188, 190], // #5F85BC
];

export const getRankColor = (rank?: number): RGBAColor => {
  switch (rank) {
    case 1:
      return postColorRange[0];
    case 2:
      return postColorRange[1];
    case 3:
      return postColorRange[2];
    case 4:
      return postColorRange[3];
    case 5:
      return postColorRange[4];
    default:
      return [0, 0, 0, 255];
  }
};

export const getPercentColor = (percent: number | null): RGBAColor => {
  if (percent == null) {
    return [0, 0, 0, 0];
  }

  if (percent <= 15) {
    return postColorRange[4];
  }

  if (percent <= 30) {
    return postColorRange[3];
  }

  if (percent <= 45) {
    return postColorRange[2];
  }

  if (percent <= 60) {
    return postColorRange[1];
  }
  if (percent <= 100 || percent > 100) {
    return postColorRange[0];
  }

  return postColorRange[4];
};

export const getPostFillColor = (d: Feature<Polygon, Partial<POSTProperties>>): RGBAColor => {
  const { priority_rank } = d.properties;
  return getRankColor(priority_rank);
};

const PRIORITY_LEVELS = [
  'Highest Priority',
  'High Priority',
  'Medium Priority',
  'Low Priority',
  'Lowest Priority',
];

export const PostLegend = (props: { selectedMap: PrimaryLayer }) => {
  const id = `${props.selectedMap}-post`;
  const primaryKey = useAtomValue(primaryKeyForSection(id)) as keyof typeof postKeyToLabel;

  return (
    <Legend
      id={id}
      title={
        primaryKey != null
          ? primaryKey === 'priority_rank'
            ? 'POST Priority Levels'
            : postKeyToLabel?.[primaryKey]
          : undefined
      }
      items={
        primaryKey === 'priority_rank'
          ? postColorRange.map((color, index) => ({
              color: `rgb(${color[0]}, ${color[1]}, ${color[2]})`,
              label: `${PRIORITY_LEVELS[index]} ${index + 1}`,
            }))
          : PERCENTILE_LEGEND_ITEMS
      }
    />
  );
};

const elevationKeys = [
  'pop',
  'ls_day_pop',
  'ls_night_pop',
  'households',
  'housingunits',
  'pop65over',
  'pop16overunemployed',
  'pop16overnotinlaborforce',
  'popamindianalaskan',
  'householdsinpoverty',
  'householdsdsfs',
  'householdsdsnofs',
  'householdswithfs',
  'householdswithpa',
  'housingunitsmobilehomes',
  'lep',
] as const;

export const postKeyToLabel = {
  event_id: 'Grouped IPAWS event',
  usng5km: '5km USNG cell',
  state: 'State',
  county: 'County',
  fips: 'County FIPS code',
  ls_day_pop: 'LandScan Daytime Population',
  ls_night_pop: 'LandScan Nighttime Population',
  pop: 'Population',
  households: 'Number of Households',
  housingunits: 'Number of Housing units',
  pop65over: 'Population age 65 and over',
  pop16overunemployed: 'Population age 16+ and unemployed',
  pop16overnotinlaborforce: 'Population 16+ not in labor force',
  popamindianalaskan: 'Population American Indian or Alaska Native',
  householdsinpoverty: 'Number of Households in poverty',
  householdsdsfs: 'Number of Households on disability and food stamps',
  householdsdsnofs: 'Number of Households on disability no food stamps',
  householdswithfs: 'Number of Households with food stamps/SNAP',
  householdswithpa: 'Number of Households with public assistance',
  housingunitsmobilehomes: 'Number of Housing units that are mobile homes',
  lep: 'Population with Limited English Proficiency',
  event: 'IPWAS event type',
  effective: 'Effective',
  expires: 'Expires',
  quantile_sum: 'Rank Sum',
  final_rank: 'Unscaled Rank',
  priority_rank: 'POST Priority',
  run_time: 'POST run time',
  CommercialOfficeImpacts: 'Commercial Office Impact',
  ExposureScore: 'Exposure Score',
  ImpactScore: 'Impact Score',
  PercentOut: 'Percent Out',
  RankPotentialPowerOutageImpact: 'Rank Potential Power Outage Impact',
  IncidentName: 'Incident Name',
  event_name: 'Event Name',
  ef_rank: 'Enhanced Fujita Rank',
  canopy_published_date: 'Canopy Published Date',
  state_intersect: 'State intersect',
  county_intersect: 'County intersect',
  fips_intersect: 'FIPS intersect',
  structures_exposed: 'Structures Exposed',
  time_category: 'Time Category',
} as const;

const postAttributes = elevationKeys.map(key => ({ key, label: postKeyToLabel[key] }));

/** The attributes a POST grid can be colored and raised by. */
export const POST_ATTRIBUTES: AttributeOptions = {
  primary: [{ key: 'priority_rank', label: 'POST Priority Levels' }, ...postAttributes],
  elevation: postAttributes,
};
